import { useState, type CSSProperties, type ReactNode } from 'react';
import { preload } from 'react-dom';
import { artUrl, bossArt, enemyArt, findItemTemplate, getItemIcon, hasArt, type ArtFolder, type ArtSize, type GearSlot, type IconSize, type NamedId, type Rarity, type StackKind, type ZoneId } from '@lastday/shared';
import { cx } from '@/lib/format';
import { PathIcon } from './Icon';
import { SLOT_ICON } from './slotIcons';

/** Icon URL for a saved item (old template ids resolve too), or null when there is no WebP icon. */
export function itemIconUrl(templateId: string | undefined, rarity: Rarity, size: IconSize): string | null {
  const t = findItemTemplate(templateId);
  return t ? getItemIcon(t, rarity, size) : null;
}

/** Starts loading icons the player will see right away (equipped gear); everything else loads lazily. */
export function preloadItemIcons(items: { templateId: string; rarity: Rarity }[], size: IconSize = 256) {
  for (const it of items) {
    const url = itemIconUrl(it.templateId, it.rarity, size);
    if (url) preload(url, { as: 'image', fetchPriority: 'high' });
  }
}

const warned = new Set<string>();
function warnMissing(what: string) {
  if (!import.meta.env.DEV || warned.has(what)) return;
  warned.add(what);
  console.warn(`[icons] missing item icon: ${what} (showing the slot icon instead)`);
}

/**
 * Item picture: the transparent WebP icons (Uncommon shows the Common picture; its green
 * comes from the frame around it). Accessories have WebP icons too since krug 5 (F10), so
 * the old SVG art is gone. A missing file falls back to the slot icon.
 *
 * `res` picks the file: 256 for inventory and gear slots, 128 for lists, feed and
 * share cards, 512 for the item detail.
 */
export function ItemIcon({
  templateId,
  rarity,
  res,
  slot,
  art,
  lazy = false,
  className,
  style,
}: {
  templateId?: string;
  rarity: Rarity;
  res: IconSize;
  /** Fallback slot when the template is unknown. */
  slot?: GearSlot;
  /** SVG art key from share cards made before the WebP icons. */
  art?: string;
  lazy?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  // Share cards from before 0.2.0 only carry the SVG key "item_{slot}_{id}": recover the item from it.
  const legacyId = !templateId && art ? /^item_[a-z]+_(.+)$/.exec(art)?.[1] : undefined;
  const t = findItemTemplate(templateId ?? legacyId);
  const src = t ? getItemIcon(t, rarity, res) : null;
  const [failed, setFailed] = useState<string | null>(null);

  if (src && failed !== src) {
    return (
      <img
        src={src}
        alt=""
        width={res}
        height={res}
        loading={lazy ? 'lazy' : 'eager'}
        decoding="async"
        draggable={false}
        onError={() => {
          warnMissing(src);
          setFailed(src);
        }}
        className={cx('block size-full select-none object-contain', className)}
        style={style}
      />
    );
  }

  if (!src && !t) warnMissing(`template "${templateId ?? '?'}"`);
  const s = t?.slot ?? slot;
  return (
    <span className={cx('flex size-full items-center justify-center text-muted', className)} style={style} aria-hidden>
      {s && <PathIcon d={SLOT_ICON[s]} size={24} className="size-[55%]" />}
    </span>
  );
}

// ---- Art set (plan v0.6, section B) ------------------------------------------------------------
// Enemies, NPC portraits, resources, backgrounds, buildings and zones: /assets/art/{folder}/{name}_{size}.webp.

const warnedArt = new Set<string>();
function warnArt(what: string) {
  if (!import.meta.env.DEV || warnedArt.has(what)) return;
  warnedArt.add(what);
  console.warn(`[art] missing picture: ${what} (showing the old icon instead)`);
}

/** URL of a picture from the art set, or null when the set has none (the caller shows its old icon). */
export function getArt<F extends ArtFolder>(folder: F, name: string | null | undefined, size: ArtSize<F>): string | null {
  if (hasArt(folder, name)) return artUrl(folder, name, size);
  warnArt(`${folder}/${name ?? '?'}`);
  return null;
}

/**
 * A picture from the art set. While the name is unknown or the file does not load, `fallback`
 * (the old icon) shows instead. Pictures load lazily unless they head the current screen (`eager`).
 * `wide` swaps in the larger file on desktop (min-width 1024px).
 */
export function ArtImage<F extends ArtFolder>({
  folder,
  name,
  size,
  wide,
  fallback = null,
  alt = '',
  eager = false,
  className,
  style,
}: {
  folder: F;
  name: string | null | undefined;
  size: ArtSize<F>;
  wide?: ArtSize<F>;
  fallback?: ReactNode;
  alt?: string;
  eager?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const src = getArt(folder, name, size);
  const [failed, setFailed] = useState<string | null>(null);
  if (!src || failed === src) return <>{fallback}</>;
  const img = (
    <img
      src={src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : undefined}
      decoding="async"
      draggable={false}
      onError={() => {
        warnArt(src);
        setFailed(src);
      }}
      className={cx('block select-none', className)}
      style={style}
    />
  );
  if (!wide || !name) return img;
  return (
    <picture className="contents">
      <source media="(min-width: 1024px)" srcSet={artUrl(folder, name, wide)} />
      {img}
    </picture>
  );
}

/**
 * Enemy or boss portrait (B1): 128 for rows and chips (shown at 32–40 px), 256 for the big
 * moments. Enemies without a picture (another survivor) show `fallback`, by default nothing.
 */
export function EnemyPortrait({ enemy, boss, zone, named, size = 128, alt = '', eager, className, fallback = null }: { enemy?: string | null; boss?: string | null; /** Package 4: a raider's faction and a drone's unit by the zone. */ zone?: ZoneId | null; /** Package 4, part D: a named enemy's own portrait. */ named?: NamedId | null; size?: 128 | 256; alt?: string; eager?: boolean; className?: string; fallback?: ReactNode }) {
  const name = boss ? bossArt(boss) : enemyArt(enemy, zone, named);
  if (!name) return <>{fallback}</>;
  return <ArtImage folder="enemies" name={name} size={size} alt={alt} eager={eager} fallback={fallback} className={cx('aspect-square shrink-0 rounded-full border border-border bg-raised object-cover', className)} />;
}

/** NPC portrait (B2): the 12 NPC faces, picked in shared `npcPortrait` / `oppPortrait` / `eventArt`. */
export function NpcPortrait({ name, size = 128, alt = '', className, fallback = null }: { name: string | null | undefined; size?: 128 | 256; alt?: string; className?: string; fallback?: ReactNode }) {
  return <ArtImage folder="npc" name={name} size={size} alt={alt} fallback={fallback} className={cx('aspect-square shrink-0 rounded-full border border-border bg-raised object-cover', className)} />;
}

/**
 * Resource, consumable and special item icon (B3). Chips and prices (up to 40 px) use the
 * 64 file, tiles 128 (256 on large screens), bigger pictures 256.
 */
export function StackArt({ kind, size = '100%', className, style }: { kind: StackKind; size?: number | string; className?: string; style?: CSSProperties }) {
  const px = typeof size === 'number' ? size : null;
  const file = px == null ? 128 : px <= 40 ? 64 : px <= 96 ? 128 : 256;
  // A size class from the caller (`size-[78%]`) wins over the default size.
  const sized = className?.includes('size-');
  const box: CSSProperties = sized ? { ...style } : { width: size, height: size, ...style };
  // Without a picture: a plain grey frame (a stack has no rarity colour, 1. 10. 2026).
  const fallback = <span className={cx('block rounded-[4px] border-2 border-line opacity-60', className)} style={box} aria-hidden />;
  return <ArtImage folder="resources" name={kind} size={file} wide={px == null ? 256 : undefined} fallback={fallback} className={cx('shrink-0 object-contain', className)} style={box} />;
}
